import { Flex, Typography } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { empty } from "../../../constants/placeholders"
import { useDateFormat } from "../../../hooks/use-date-format.ts"
import { cn } from "../../../utils/cn.ts"
import { CalendarIcon, ClockIcon } from "../../Lead/icons/lead-state-icons.tsx"
import { ActivityQuickDetailsPopover } from "./ActivityQuickDetails/ActivityQuickDetailsPopover.tsx"
import {
  type FollowUpState,
  followUpState,
  formatFollowUp,
  isFollowUpOverdue
} from "../helpers/lead-table-format.ts"

const { Text } = Typography

/** From the client's icon pack: red clock, orange clock, blue clock, purple calendar. */
const FOLLOW_UP_STYLES: Record<
  FollowUpState,
  { icon: typeof ClockIcon; color: string }
> = {
  due: { icon: ClockIcon, color: "#EF4444" },
  upcoming: { icon: ClockIcon, color: "#F59E0B" },
  scheduled: { icon: ClockIcon, color: "#2563EB" },
  rehash: { icon: CalendarIcon, color: "#8B5CF6" }
}

interface LeadFollowUpCellProps {
  date?: string | null
  time?: string | null
  /** Nurturing leads only date the return — rendered as "Rehash: <date>". */
  nurture?: boolean
  /** The linked calendar event, and where clicking the cell should take the
   *  user — quick details when there is something to show, otherwise the
   *  lead's own edit affordance (the table has no inline follow-up editor). */
  eventId?: string | null
  ownerName?: string | null
  /** Omitted where the caller has no edit affordance to hand off to — the
   *  popover then shows details only, with no Edit action. */
  onEdit?: () => void
  /** Fires after the linked event is marked complete, so the row can refresh. */
  onCompleted?: () => void
}

export function LeadFollowUpCell({
  date,
  time,
  nurture,
  eventId,
  ownerName,
  onEdit,
  onCompleted
}: LeadFollowUpCellProps) {
  const { t } = useTranslation()
  const { userFormat } = useDateFormat()
  const [open, setOpen] = useState(false)
  if (!date) return <>{empty}</>

  const isRehash = !!nurture
  const label = formatFollowUp(date, time, { isRehash, userFormat })
  const overdue = isFollowUpOverdue(date, undefined, userFormat)
  const state = followUpState(date, { isRehash, userFormat })

  if (!state) return <>{empty}</>

  const { icon: StateIcon, color } = FOLLOW_UP_STYLES[state]

  return (
    <ActivityQuickDetailsPopover
      kind={"followUp"}
      eventId={eventId}
      overdue={overdue}
      fallbackDateLabel={label}
      fallbackOwnerLabel={ownerName}
      open={open}
      onOpenChange={setOpen}
      onEdit={onEdit}
      onCompleted={onCompleted}
    >
      <Flex vertical gap={0} className={"cursor-pointer"}>
        <Flex align={"center"} gap={6}>
          <StateIcon className={"shrink-0 text-sm"} style={{ color }} />
          <Text
            className={cn(overdue && "text-error")}
            ellipsis={{ tooltip: label }}
          >
            {label}
          </Text>
        </Flex>
        {/* Colour is never the only signal, so the word carries it too. */}
        {overdue && (
          <Text type={"danger"} className={"text-xs pl-5"}>
            {t("leadsTable.row.overdue")}
          </Text>
        )}
      </Flex>
    </ActivityQuickDetailsPopover>
  )
}
